import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '@/redux/store'
import { selectAiSettingsEnabled } from '@/redux/ducks/tenantUser/settings'
import type { AiTextTransformProps } from '@ai-chat-widget-contracts/contracts'
import { useAiWidgetRuntime } from './runtime'
import AiWidgetErrorBoundary from './error-boundary'
import { registerTextTransformTarget } from './ai-text-transform-selection-controller'
import { AiTextTransformLegacyBridge } from './ai-text-transform-legacy-bridge'

type BridgeProps = Omit<AiTextTransformProps, 'runtime'>
const RemoteAiTextTransform = React.lazy(() => import('aiChatWidget/AiTextTransform'))

const RemoteAiTextTransformBridge = (props: BridgeProps) => {
  const runtime = useAiWidgetRuntime()
  const propsRef = useRef(props)
  const [activation, setActivation] = useState({ active: false, signal: 0 })
  propsRef.current = props

  useEffect(() => {
    if (!props.enabled) {
      setActivation(current => current.active ? { ...current, active: false } : current)
      return
    }

    return registerTextTransformTarget(
      () => propsRef.current,
      (active, signal) => setActivation(current =>
        current.active === active && (!active || current.signal === signal)
          ? current
          : { active, signal }),
    )
  }, [props.enabled])

  if (!props.enabled) return null
  if (!activation.active) return null
  return <Suspense fallback={null}><RemoteAiTextTransform runtime={runtime} {...props} activationSignal={activation.signal} /></Suspense>
}

export const AiTextTransformBridge = (props: BridgeProps) => {
  const globallyEnabled = useAppSelector(selectAiSettingsEnabled)
  const resolvedProps = { ...props, enabled: props.enabled && globallyEnabled }
  return (
    <AiWidgetErrorBoundary fallback={<AiTextTransformLegacyBridge {...resolvedProps} />}>
      <RemoteAiTextTransformBridge {...resolvedProps} />
    </AiWidgetErrorBoundary>
  )
}

export const useAiTextTransform = (props: BridgeProps) => ({
  renderAiComponents: useCallback(() => <AiTextTransformBridge {...props} />, [props]),
})
